import type { ExtendedPlaceholderContentItemModel } from 'autogen/swagger/all';
import ArticleCard from 'components/ArticleCard';
import { useRef } from 'react';
import styled from 'styled-components';
import { Slide } from 'components/emblaCarousel';
import useAdformImpressionTracking from 'hooks/tracking/useAdformImpressionTracking';
import { postAdformClickTracking } from 'modules/tracking/adformClickTracking';
import { PageActionArticleCard } from 'components/ArticleCard/ArticleCard';
import DynamicImageRenderer from 'components/generic/Image/DynamicImageRenderer';
import mapResponsiveImageModelToPictureProps from 'components/generic/picture/helpers/mappingHelper';

const StyledSlide = styled(Slide)`
    height: 360px;
`;

const StyledPageActionArticleCard = styled(PageActionArticleCard)`
    width: 100%;
    height: 100%;
`;

const StyledStaticArticleCard = styled(ArticleCard)`
    width: 100%;
    height: 100%;
`;

const ImageContainer = styled.div`
    height: 200px;
    overflow: hidden;
    display: flex;
    justify-content: center;
    align-items: center;

    & img {
        min-height: 100%;
        object-fit: cover;
        min-width: 100%;
        max-width: none;
    }
`;

const Image = styled(DynamicImageRenderer)`
    height: 100%;
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
`;

export default function ArticleContentItem({
    item,
    ...restProps
}: {
    item: ExtendedPlaceholderContentItemModel;
}): JSX.Element {
    const {
        heading,
        dataLayer,
        pageAction,
        overline,
        buttonText,
        image,
        adformImpTrackingUrls,
        adformClickTrackingUrl
    } = item;
    const ref = useRef(null);

    useAdformImpressionTracking({
        ref,
        impTrackingUrls: adformImpTrackingUrls
    });

    if (!pageAction) {
        return (
            <StyledSlide data-datalayer={JSON.stringify(dataLayer)} {...restProps}>
                <StyledStaticArticleCard
                    ref={ref}
                    onClick={adformClickTrackingUrl ? () => postAdformClickTracking(adformClickTrackingUrl) : undefined}
                    customTheme={{ layoutTheme: 'vertical' }}
                    textBox={{
                        title: heading || '',
                        overline,
                        action: {
                            buttonText
                        }
                    }}
                >
                    <ImageContainer>
                        {image && <Image {...mapResponsiveImageModelToPictureProps(image, 250)} />}
                    </ImageContainer>
                </StyledStaticArticleCard>
            </StyledSlide>
        );
    }

    return (
        <StyledSlide data-datalayer={JSON.stringify(dataLayer)} {...restProps}>
            <StyledPageActionArticleCard
                ref={ref}
                action={pageAction}
                onClick={adformClickTrackingUrl ? () => postAdformClickTracking(adformClickTrackingUrl) : undefined}
                customTheme={{ layoutTheme: 'vertical' }}
                textBox={{
                    title: heading || '',
                    overline,
                    action: {
                        buttonText
                    }
                }}
            >
                <ImageContainer>
                    {image && <Image {...mapResponsiveImageModelToPictureProps(image, 250)} />}
                </ImageContainer>
            </StyledPageActionArticleCard>
        </StyledSlide>
    );
}
